import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const STORAGE_KEY = 'wcfantasy.token';

interface AuthState {
  token: string | null;
  user: string | null;
  setSession: (token: string, name: string) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

let _token: string | null = null;
try { _token = localStorage.getItem(STORAGE_KEY); } catch {}

function decodeJwtPayload(payload: string): { sub?: unknown; exp?: unknown } | null {
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

let _user: string | null = null;
if (_token) {
  const [, payload] = _token.split('.');
  const json = decodeJwtPayload(payload ?? '');
  if (json && typeof json.sub === 'string' && typeof json.exp === 'number' && json.exp * 1000 > Date.now()) {
    _user = json.sub;
  } else {
    _token = null;
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }
}

export function getToken(): string | null { return _token; }

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [token, setToken] = useState(_token);
  const [user, setUser] = useState(_user);

  useEffect(() => {
    _token = token;
    _user = user;
  }, [token, user]);

  function setSession(t: string, name: string) {
    try { localStorage.setItem(STORAGE_KEY, t); } catch {}
    setToken(t);
    setUser(name);
    qc.invalidateQueries();
  }

  function signOut() {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setToken(null);
    setUser(null);
    qc.invalidateQueries();
  }

  return (
    <AuthContext.Provider value={{ token, user, setSession, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
